// src/pages/creator-panel/LivePhoneCall/CallRecordings.js
// Dedicated page listing all Live Phone Call recordings for a chatbot.
// Audio playback uses a 1-hour signed URL fetched on demand — the WAVs
// themselves live in GCS and are not publicly accessible.
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../utils/axiosInstance';
import { useChatbots } from '../../../contexts/ChatbotContext';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    Box, Typography, Alert, Button, CircularProgress, Card, CardContent,
    IconButton, Tooltip, TextField, InputAdornment, Pagination,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    PlayCircle as PlayIcon,
    Delete as DeleteIcon,
    FiberManualRecord as RecordIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

const PAGE_SIZE = 25;

export default function CallRecordings() {
    const navigate = useNavigate();
    const { chatbotId: urlChatbotId } = useParams();
    const { activeChatbot } = useChatbots();
    const chatbotId = urlChatbotId || activeChatbot?.chatbot_identity;
    const scope = `/chatbot/${chatbotId}`;

    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPerm, setCheckingPerm] = useState(true);

    const [recordings, setRecordings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [playingUrl, setPlayingUrl] = useState('');
    const [playingId, setPlayingId] = useState(null);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            const v = await hasPermission('live-phone-call', 'view');
            const a = await hasPermission('live-phone-call', 'add');
            const e = await hasPermission('live-phone-call', 'edit');
            const d = await hasPermission('live-phone-call', 'delete');
            if (!active) return;
            setCanView(v);
            setCanModify(a || e || d);
            setCheckingPerm(false);
        })();
        return () => { active = false; };
    }, []);

    const fetchRecordings = useCallback(async (pageNum = 1) => {
        if (!chatbotId) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(
                `/api/chatbots/${chatbotId}/live-phone-call/recordings`,
                { params: { page: pageNum, limit: PAGE_SIZE } },
            );
            setRecordings(res?.data?.data || []);
            setTotal(res?.data?.total || 0);
            setPage(pageNum);
        } catch (e) {
            setMessage({ kind: 'error', text: 'Could not load recordings.' });
        } finally {
            setLoading(false);
        }
    }, [chatbotId]);

    useEffect(() => {
        if (!checkingPerm && canView) fetchRecordings(1);
    }, [checkingPerm, canView, fetchRecordings]);

    const handlePlay = async (rec) => {
        if (playingId === rec.id && playingUrl) {
            setPlayingId(null);
            setPlayingUrl('');
            return;
        }
        try {
            const res = await axiosInstance.get(`/api/live-phone-call/recordings/${rec.id}/url`);
            const url = res?.data?.data?.url;
            if (url) {
                setPlayingUrl(url);
                setPlayingId(rec.id);
            } else {
                setMessage({ kind: 'error', text: 'Could not generate playback URL.' });
            }
        } catch (e) {
            setMessage({ kind: 'error', text: 'Could not load recording.' });
        }
    };

    const handleDelete = async (rec) => {
        if (!window.confirm(`Delete recording from ${rec.created_at ? new Date(rec.created_at).toLocaleString() : ''}?`)) return;
        try {
            await axiosInstance.delete(`/api/live-phone-call/recordings/${rec.id}`);
            setRecordings(recordings.filter(r => r.id !== rec.id));
            setTotal(t => Math.max(0, t - 1));
            if (playingId === rec.id) {
                setPlayingId(null);
                setPlayingUrl('');
            }
        } catch (e) {
            setMessage({ kind: 'error', text: 'Could not delete recording.' });
        }
    };

    const formatDuration = (s) => {
        if (s == null) return '—';
        const m = Math.floor(s / 60);
        const ss = (s % 60).toString().padStart(2, '0');
        return `${m}:${ss}`;
    };

    const formatBytes = (b) => {
        if (!b) return '';
        if (b < 1024) return `${b} B`;
        if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
        return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Client-side filter (results-on-page only). For server-side search we'd
    // pass a `q` param to the API — adding it later if the list grows large.
    const filtered = search
        ? recordings.filter(r => {
            const hay = `${r.caller_number || ''} ${r.callee_number || ''} ${r.call_sid || ''} ${r.provider || ''}`.toLowerCase();
            return hay.includes(search.toLowerCase());
        })
        : recordings;

    if (checkingPerm) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    if (!canView) return <Alert severity="warning" sx={{ m: 3 }}>You don't have permission to view call recordings.</Alert>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate(`${scope}/live-phone-call`)}>
                        <BackIcon />
                    </IconButton>
                    <RecordIcon color="error" />
                    <Typography variant="h5">Call Recordings</Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} onClick={() => fetchRecordings(page)} disabled={loading}>
                    Refresh
                </Button>
            </Box>

            {message && (
                <Alert severity={message.kind} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        All Live Phone Call audio is recorded as stereo WAV (caller left, AI right)
                        and stored in Google Cloud Storage. Playback uses a short-lived signed URL.
                    </Typography>

                    <TextField
                        fullWidth size="small"
                        placeholder="Search by caller number, call SID, or provider…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                        sx={{ mb: 2 }}
                    />

                    {loading ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
                    ) : filtered.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                            {search ? 'No recordings match your search.' : 'No recordings yet.'}
                        </Typography>
                    ) : (
                        filtered.map(rec => (
                            <Box key={rec.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Tooltip title={playingId === rec.id ? 'Stop' : 'Play'}>
                                    <IconButton onClick={() => handlePlay(rec)} color={playingId === rec.id ? 'secondary' : 'primary'}>
                                        <PlayIcon fontSize="large" />
                                    </IconButton>
                                </Tooltip>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle2" noWrap>
                                        {rec.caller_number || 'Unknown caller'} → {rec.callee_number || '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {rec.created_at ? new Date(rec.created_at).toLocaleString() : '—'}
                                        {' · '}Duration {formatDuration(rec.duration_seconds)}
                                        {' · '}Provider {rec.provider || '—'}
                                        {rec.size_bytes ? ` · ${formatBytes(rec.size_bytes)}` : ''}
                                    </Typography>
                                    {rec.call_sid && (
                                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', display: 'block' }}>
                                            {rec.call_sid}
                                        </Typography>
                                    )}
                                </Box>
                                {canModify && (
                                    <Tooltip title="Delete recording">
                                        <IconButton onClick={() => handleDelete(rec)} size="small">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        ))
                    )}

                    {playingUrl && (
                        <Box sx={{ mt: 3, position: 'sticky', bottom: 16, bgcolor: 'background.paper', borderRadius: 1, p: 1, boxShadow: 2 }}>
                            <audio
                                key={playingUrl}
                                src={playingUrl}
                                controls
                                autoPlay
                                style={{ width: '100%' }}
                                onEnded={() => { setPlayingId(null); setPlayingUrl(''); }}
                            />
                        </Box>
                    )}

                    {total > PAGE_SIZE && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={Math.ceil(total / PAGE_SIZE)}
                                page={page}
                                onChange={(_, p) => fetchRecordings(p)}
                                color="primary"
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
